import { useMemo, useState } from "react";
import { ICONES, Icone, corAvatar, tinta } from "../visual.jsx";
import { useCelular, useEstado, useTablet } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { contagem, MenuDeAcoes, Rosto, RostoDaIa } from "./comuns.jsx";
import { NA_IA, linhaDaIa } from "../mesa-painel.js";
import { SEM_CRIATIVO } from "../criativos.js";
import { conectado, numeroCurto, numeroLegivel } from "../whatsapp-estado.js";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";

/**
 * O valor que a URL usa para "ninguém": a fila é uma linha de verdade, e
 * `?resp=` vazio significaria "sem recorte". MESMO literal de `SEM_RESPONSAVEL`
 * em src/leads.ts — os dois lados leem a mesma URL.
 */
export const SEM_RESPONSAVEL = "__fila__";

/*
 * A PÍLULA do filtro: 32 de altura, recuo 14, 13/600, o número numa cápsula de
 * 20 — a mesma pele das `Fichas` da casa, escrita aqui porque estas pastilhas
 * não são uma escolha única (cada uma liga e desliga sozinha, `aria-pressed`) e
 * o `ToggleGroup` das Fichas exige uma marcada por vez. `alvo` dá os 40/44 de
 * toque sem crescer o desenho: eram 26px, menores que a ponta do dedo, na
 * pastilha que muda o recorte da mesa inteira. Ligada é tinta cheia.
 */
const PILULA =
  "group alvo flex h-8 flex-none cursor-pointer touch-manipulation items-center gap-2 rounded-full border border-borda-controle bg-card px-3.5 text-[13px] font-semibold whitespace-nowrap text-tinta-media hover:bg-foreground/4 hover:text-foreground aria-pressed:border-tinta aria-pressed:bg-tinta aria-pressed:text-mesa aria-pressed:hover:bg-tinta/86 aria-pressed:hover:text-mesa";

/* A cápsula do número dentro da pílula; na ligada ela vira véu da mesa. */
const CAPSULA =
  "-mr-1.5 inline-grid h-5 min-w-[22px] place-items-center rounded-full bg-foreground/8 px-1.5 text-selo leading-none font-bold tabular-nums text-tinta-suave group-aria-pressed:bg-mesa/20 group-aria-pressed:text-mesa";

/* O rosto escolhido se diz por um aro de 2px separado do retrato por 2px da cor
   da coluna — em `box-shadow`, e não em `border`: a borda somava 4px ao rosto
   (34 viravam 38) e o aro, igual ao anel de foco, não mexe na medida. */
const ARO_DO_ROSTO = "shadow-[0_0_0_2px_var(--color-secondary),0_0_0_4px_var(--color-foreground)]";
const ARO_DO_HOVER =
  "hover:shadow-[0_0_0_2px_var(--color-secondary),0_0_0_4px_color-mix(in_oklab,var(--color-foreground)_20%,transparent)]";

/**
 * O filtro da mesa — versão "Equipe + funil" (mockup A, escolhido em 26/08).
 *
 * Eram sete pastilhas e uma grade escondida num balão que, de quebra, não
 * abria. Agora é UM filtro, sempre à vista, que responde às duas perguntas do
 * dia: DE QUEM é (a fileira de rostos, com a carga de cada um; a fila é o
 * círculo tracejado) e EM QUE ETAPA está (o funil como barra colorida, cada
 * segmento na proporção de casos). Toque num rosto, num segmento, ou nos dois;
 * o recorte fica ESCRITO logo abaixo, com o ✕ — e mora na URL (`?etapa=`,
 * `?resp=`), então "os casos da Camila em aguardando documentos" se manda por
 * mensagem.
 *
 * Os números vêm do banco (`matrizDaMesa`, em src/leads.ts), contados sobre a
 * busca atual — nunca da página carregada. Sobrou uma pastilha só, "esperando
 * resposta": é a fila do dia, e não cabe em rosto nem em etapa.
 */
export default function FiltroDaMesa() {
  const { matriz, etapas, equipe, contadores, catalogoDeTags, numerosDoSetor } = useEstado();
  const celular = useCelular();
  const tablet = useTablet();
  const { etapaDaMesa, respDaMesa, tagDaMesa, criativoDaMesa, numeroDaMesa, filtro, ajustar } = useNavegacao();

  const { pessoas, segmentos, noRecorte, rostosPorEtapa } = useMemo(() => {
    const porCelula = new Map(matriz.map((c) => [`${c.stage}|${c.resp}`, c.n]));
    const porResp = new Map();
    const porEtapa = new Map();
    for (const c of matriz) {
      porResp.set(c.resp, (porResp.get(c.resp) ?? 0) + c.n);
      porEtapa.set(c.stage, (porEtapa.get(c.stage) ?? 0) + c.n);
    }
    const respChave = respDaMesa === SEM_RESPONSAVEL ? "" : respDaMesa;
    const ativos = new Map((equipe ?? []).map((p) => [p.nome.toUpperCase(), p]));
    /* Quem aparece: toda pessoa ATIVA do cadastro (mesmo com zero — carga zero é
       informação) mais quem tem caso e não está no cadastro, mais a fila. */
    const nomes = new Set([...ativos.keys(), ...[...porResp.keys()].filter((k) => k && k !== NA_IA)]);
    const pessoas = [...nomes]
      .map((nome) => {
        const p = ativos.get(nome);
        const n = etapaDaMesa ? (porCelula.get(`${etapaDaMesa}|${nome}`) ?? 0) : (porResp.get(nome) ?? 0);
        return { chave: nome, nome: p?.nome ?? nome, foto: p?.avatar, cor: p?.cor, n, total: porResp.get(nome) ?? 0 };
      })
      .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome));
    const fila = {
      chave: SEM_RESPONSAVEL,
      nome: "Fila",
      n: etapaDaMesa ? (porCelula.get(`${etapaDaMesa}|`) ?? 0) : (porResp.get("") ?? 0),
    };
    /* A fila da IA logo depois da fila de gente: as duas são "ainda não está
       com ninguém do escritório", cada uma por um motivo. */
    const ia = linhaDaIa(porResp.get(NA_IA) ?? 0).map((l) => ({
      ...l,
      n: etapaDaMesa ? (porCelula.get(`${etapaDaMesa}|${NA_IA}`) ?? 0) : l.total,
    }));
    const segmentos = (etapas ?? []).map((e) => ({
      ...e,
      n: respDaMesa ? (porCelula.get(`${e.chave}|${respChave}`) ?? 0) : (porEtapa.get(e.chave) ?? 0),
    }));
    const noRecorte = matriz
      .filter((c) => (!etapaDaMesa || c.stage === etapaDaMesa) && (!respDaMesa || c.resp === respChave))
      .reduce((s, c) => s + c.n, 0);
    /* Quem está com caso em cada etapa — os rostos da lista detalhada. */
    const rostosPorEtapa = new Map(
      (etapas ?? []).map((e) => [
        e.chave,
        [fila, ...ia, ...pessoas]
          .map((p) => ({ ...p, n: porCelula.get(`${e.chave}|${p.chave === SEM_RESPONSAVEL ? "" : p.chave}`) ?? 0 }))
          .filter((p) => p.n > 0),
      ]),
    );
    return { pessoas: [fila, ...ia, ...pessoas], segmentos, noRecorte, rostosPorEtapa };
  }, [matriz, etapas, equipe, etapaDaMesa, respDaMesa]);

  /*
   * O mapa das etapas FLUTUA (`Popover`), e não abre empurrando a lista.
   *
   * Ele era uma seção que se abria dentro do topo da coluna, com o aberto
   * lembrado no navegador. Com as onze etapas da produção eram ~400px no
   * cabeçalho, que é parte fixa de uma coluna `overflow:hidden`: a lista
   * (`ListaLonga`, `flex-1 min-h-0`) ficava com o resto. Num notebook de 768 de
   * altura escolher uma etapa (que acende a linha "Mostrando") deixava a lista
   * com 12px — havia conversa embaixo e nenhuma rolagem visível (relato do
   * Felipe em 08/10). Flutuando, o topo tem altura fixa de novo, escolher fecha
   * o mapa, e não há "aberto" guardado para a pessoa reencontrar amanhã.
   */
  const [detalhe, setDetalhe] = useState(false);
  const escolherNoMapa = (mudanca) => {
    ajustar(mudanca);
    setDetalhe(false);
  };
  const escolherPessoa = (chave) => ajustar({ resp: respDaMesa === chave ? null : chave });
  const escolherEtapa = (chave) => ajustar({ etapa: etapaDaMesa === chave ? null : chave });
  const ativo = Boolean(etapaDaMesa || respDaMesa || tagDaMesa || criativoDaMesa || numeroDaMesa);
  /* O número escolhido no recorte (`?numero=` é o id da conexão). */
  const numeroMarcado = (numerosDoSetor ?? []).find((n) => String(n.id) === numeroDaMesa);
  const variosNumeros = (numerosDoSetor?.length ?? 0) > 1;
  const rotuloEtapa = segmentos.find((e) => e.chave === etapaDaMesa)?.rotulo ?? etapaDaMesa;
  const nomeResp = pessoas.find((p) => p.chave === respDaMesa)?.nome ?? respDaMesa;
  const esperando = contadores["Prazo hoje"] ?? 0;
  const soEsperando = filtro === "Prazo hoje";
  /* O ARQUIVO como pastilha, e não como interruptor escondido: arquivar
     prometia "dá para trazer de volta" e não havia tela nenhuma que o mostrasse
     — a única pista de que o caso existia era ele não estar na lista. Sendo
     recorte, ele ganha contagem, URL (`?filtro=Arquivados`) e matriz de graça,
     como qualquer outro. Só aparece com caso dentro: pastilha que diz zero é
     um controle a mais na fileira mais cheia da mesa. */
  const arquivados = contadores.Arquivados ?? 0;
  const soArquivados = filtro === "Arquivados";
  /*
   * Quantos rostos cabem na fileira sem ela rolar nem quebrar.
   *
   * Três (dois no tablet): cada rosto agora é uma PÍLULA com o número ao lado
   * (≈66px), e não o rosto de 28 com o número pendurado no canto. Pendurado,
   * com a produção de verdade (centenas de casos por pessoa), o "99+" de 30px
   * tapava o próprio rosto e encostava no do vizinho — a fileira virava uma
   * pilha de selos pretos. Ao lado ele nunca cobre ninguém. É medida do
   * desenho, não conta de execução: medir a largura faria a fileira mudar de
   * tamanho enquanto a pessoa arrasta a divisória. O resto mora no "+N".
   */
  const TETO_DE_ROSTOS = tablet ? 2 : 3;
  /* O escolhido nunca vai para dentro do menu: esconder o recorte que está
     aplicado é a tela mentindo sobre o que está mostrando. */
  /* A IA ocupa uma das vagas (ela vem logo depois da fila): entrar além do teto
     empurrava o "+N" para fora da coluna — a fileira é de largura fixa. */
  const cabem = TETO_DE_ROSTOS + 1;
  const naFileira = pessoas
    .slice(0, cabem)
    .concat(
      respDaMesa && !pessoas.slice(0, cabem).some((p) => p.chave === respDaMesa)
        ? pessoas.filter((p) => p.chave === respDaMesa)
        : [],
    );
  const noMenu = pessoas.filter((p) => !naFileira.includes(p));
  const visiveis = segmentos.filter((e) => e.n > 0 || e.chave === etapaDaMesa);
  const somaVisivel = visiveis.reduce((s, e) => s + e.n, 0) || 1;

  /* A etiqueta escolhida, com a cor do catálogo — é ela que o gatilho veste. */
  const tagMarcada = (catalogoDeTags ?? []).find((c) => c.tag.toLowerCase() === tagDaMesa.toLowerCase());

  return (
    <div className="mt-2.5">
      {/*
       * A fileira de rostos: UMA LINHA, sempre — nem rolagem, nem quebra.
       *
       * Ela rolava de lado com sete pessoas, e contêiner que rola no eixo X
       * recorta no Y também; quebrar em duas linhas fazia a altura do topo
       * mudar com o tamanho da equipe. Então cabe o que cabe e o resto entra no
       * "+N", que abre a lista inteira com nome e número escritos.
       *
       * Cada rosto é uma pílula com a carga AO LADO (o mesmo desenho da fila):
       * o número pendurado no canto, com "99+", cobria o rosto de quem ele
       * contava e o do vizinho.
       */}
      <div role="group" aria-label="Filtrar por responsável" className="flex items-center gap-1.5">
        {naFileira.map((p) => {
          const marcado = respDaMesa === p.chave;
          const fila = p.chave === SEM_RESPONSAVEL;
          const ia = p.chave === NA_IA;
          return (
            <button
              key={p.chave}
              onClick={() => escolherPessoa(p.chave)}
              aria-pressed={marcado}
              aria-label={`${p.nome}: ${p.n} ${p.n === 1 ? "atendimento" : "atendimentos"}`}
              data-dica={
                fila
                  ? `Fila — ${p.n} sem responsável`
                  : ia
                    ? `IA — ${p.n} sendo conduzido(s) pela IA`
                    : `${p.nome} — ${p.n} no recorte`
              }
              /* A fila é tracejada e terra (não é pessoa: é quem ainda não tem
                 dono); a pessoa é a pílula da casa com o rosto dentro. Marcada,
                 as duas ganham o aro de 2px — em sombra, para não somar medida. */
              className={cn(
                "alvo flex h-8 flex-none cursor-pointer touch-manipulation items-center gap-1 rounded-full bg-transparent py-0 pr-2 transition-shadow",
                fila
                  ? "border-[1.5px] border-dashed border-terra/50 pl-2.5 text-apoio font-semibold text-terra"
                  : "border border-borda-controle bg-card pl-0.5",
                marcado ? ARO_DO_ROSTO : ARO_DO_HOVER,
              )}
            >
              {fila ? "Fila" : ia ? <RostoDaIa tamanho={28} /> : <Rosto pessoa={p.nome} foto={p.foto} cor={p.cor} tamanho={28} />}
              <span
                className={cn(
                  "min-w-[18px] text-center font-mono text-selo font-bold tabular-nums",
                  fila ? "text-terra" : p.n ? "text-foreground" : "text-tinta-fraca",
                )}
              >
                {contagem(p.n)}
              </span>
            </button>
          );
        })}
        {/* O que não coube: a lista inteira, com nome e carga escritos — que é
            mais legível que o rosto, e é onde se procura alguém pelo nome. */}
        {noMenu.length > 0 && (
          <MenuDeAcoes
            rotulo={`Mais ${noMenu.length} pessoa(s) da equipe`}
            titulo="Filtrar por quem cuida"
            legenda="Quem cuida"
            largura="240px"
            altura="360px"
            classe="flex h-8 min-w-8 flex-none items-center justify-center rounded-full border border-dashed border-borda-controle px-1.5 text-selo font-bold text-tinta-suave hover:bg-foreground/4 hover:text-foreground data-[popup-open]:bg-foreground/8"
            itens={noMenu.map((p) => ({
              chave: p.chave,
              rotulo: p.nome,
              detalhe: `${p.n} no recorte`,
              marca: respDaMesa === p.chave ? "✓" : undefined,
              aoTocar: () => escolherPessoa(p.chave),
            }))}
          >
            +{noMenu.length}
          </MenuDeAcoes>
        )}
      </div>

      {/*
        No TELEFONE o funil vira fileira de pastilhas ESCRITAS.
        A barra proporcional se lê pela dica (`data-dica`), e dica é hover — que
        em tela de toque não existe. A pastilha diz o nome, cabe no dedo e rola
        de lado, como a fileira de filtros de qualquer aplicativo. O que rola
        CONFESSA que rola: a ponta direita esmaece (`mask-image`), e a pastilha
        cortada debaixo do esmaecido é a pista de que há mais.
      */}
      {celular ? (
        <div
          role="group"
          aria-label="Filtrar por etapa"
          /* `py-1.5`: o alvo invisível de 44 passa 6px da pílula de 32, e numa
             fileira que rola de lado o que sobra na vertical vira rolagem. */
          className="-mx-4 mt-1.5 flex gap-1.5 overflow-x-auto px-4 py-1.5 [mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)] [scrollbar-width:none]"
        >
          {visiveis.map((e) => {
            const marcado = etapaDaMesa === e.chave;
            const cor = e.cor || corAvatar(e.chave);
            return (
              <button key={e.chave} onClick={() => escolherEtapa(e.chave)} aria-pressed={marcado} className={PILULA}>
                <span className="size-2 flex-none rounded-full" style={{ background: cor }} />
                <span>{e.rotulo}</span>
                <span className={CAPSULA}>{contagem(e.n)}</span>
              </button>
            );
          })}
          {!visiveis.length && <span className="font-mono text-selo text-tinta-fraca">sem casos neste recorte</span>}
        </div>
      ) : (
        <div className="mt-2.5 flex items-center gap-1.5">
          {/* O funil: cada etapa na proporção de casos. Cor do esquema (stages.cor)
              ou estável pela chave — valor de dado, logo inline. 24 de altura e
              raio de pílula, o idioma do trilho do processo.

              Todo segmento tem largura para o próprio número (`min-w-[30px]`):
              com 1.325 casos e 972 numa etapa só, a proporção pura deixava as
              outras como fiapos de 6px sem número nenhum — a barra dizia "uma
              etapa e um arco-íris". A proporção cede um pouco; a leitura volta. */}
          <div
            role="group"
            aria-label="Filtrar por etapa"
            className="flex h-6 min-w-0 flex-1 gap-[2px] overflow-hidden rounded-full"
          >
            {visiveis.length ? (
              visiveis.map((e) => {
                const marcado = etapaDaMesa === e.chave;
                const cor = e.cor || corAvatar(e.chave);
                return (
                  <button
                    key={e.chave}
                    onClick={() => escolherEtapa(e.chave)}
                    aria-pressed={marcado}
                    aria-label={`${e.rotulo}: ${e.n}`}
                    data-dica={`${e.rotulo} — ${e.n}`}
                    /* Marcado e foco moram POR DENTRO, porque a barra é
                       `overflow:hidden` (é o que arredonda as pontas) e o anel de
                       fora era cortado em cima e embaixo. */
                    className={`relative min-w-[30px] cursor-pointer border-0 p-0 transition-opacity focus-visible:outline-2! focus-visible:-outline-offset-4! focus-visible:outline-anel! ${
                      marcado ? "opacity-100 shadow-[inset_0_0_0_2px_var(--color-foreground)]" : "opacity-85 hover:opacity-100"
                    }`}
                    style={{ flex: `${Math.max(e.n, 0.6)} 1 0`, background: cor }}
                  >
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-selo font-bold text-sobre-escuro tabular-nums">
                      {e.n}
                    </span>
                  </button>
                );
              })
            ) : (
              <span className="flex-1 rounded-full bg-foreground/8 text-center font-mono text-apoio leading-6 text-tinta-fraca">
                sem casos neste recorte
              </span>
            )}
          </div>
          {/* A legenda da barra mora AO LADO dela: era uma linha inteira só para
              a palavra "etapas", entre a barra e as etiquetas. */}
          <Popover open={detalhe} onOpenChange={setDetalhe}>
            <PopoverTrigger
              aria-label={detalhe ? "Esconder as etapas" : "Ver as etapas por nome"}
              data-dica={detalhe ? undefined : "Ver as etapas por nome"}
              className="alvo flex size-6 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-tinta-fraca hover:bg-foreground/6 hover:text-foreground data-popup-open:bg-foreground/8 data-popup-open:text-foreground"
            >
              <span className={`inline-flex transition-transform ${detalhe ? "rotate-180" : ""}`}>
                <Icone de={ICONES.baixo} tamanho={16} />
              </span>
            </PopoverTrigger>
            <PopoverContent
              id="detalhe-do-funil"
              side="bottom"
              align="end"
              aria-label="Etapas do funil"
              /* O teto é da JANELA, e o mapa rola por dentro: com a lista
                 inteira de etapas e uma janela baixa ele não pode sair da tela. */
              className="w-[340px] max-w-[92vw] gap-[3px] overflow-y-auto rounded-cartao p-1.5"
              style={{ maxHeight: "min(70vh, 520px)" }}
            >
              {segmentos.map((e) => {
                const cor = e.cor || corAvatar(e.chave);
                const marcada = etapaDaMesa === e.chave;
                const rostos = rostosPorEtapa.get(e.chave) ?? [];
                return (
                  <div
                    key={e.chave}
                    className={`grid grid-cols-[6px_1fr_auto] items-center gap-2 rounded-controle px-1.5 py-[4px] ${
                      marcada ? "bg-foreground/8" : "hover:bg-foreground/4"
                    } ${e.n ? "" : "opacity-50"}`}
                  >
                    <span className="h-[22px] w-[6px] rounded-full" style={{ background: cor }} />
                    <button
                      onClick={() => escolherNoMapa({ etapa: etapaDaMesa === e.chave ? null : e.chave })}
                      aria-pressed={marcada}
                      className="min-w-0 cursor-pointer rounded-etiqueta border-0 bg-transparent p-0 text-left"
                    >
                      <span className="block truncate text-apoio font-semibold">{e.rotulo}</span>
                      <span className="mt-[3px] block h-[4px] overflow-hidden rounded-full bg-foreground/8">
                        <span
                          className="block h-full rounded-full"
                          style={{ width: `${Math.round((100 * e.n) / somaVisivel)}%`, background: cor }}
                        />
                      </span>
                    </button>
                    <span className="flex items-center">
                      <span className="flex">
                        {rostos.slice(0, 5).map((p, i) => (
                          <button
                            key={p.chave}
                            onClick={() =>
                              escolherNoMapa({ etapa: e.chave, resp: respDaMesa === p.chave && marcada ? null : p.chave })
                            }
                            aria-label={`${p.nome} em ${e.rotulo}: ${p.n}`}
                            data-dica={`${p.nome} · ${e.rotulo}: ${p.n}`}
                            className={`cursor-pointer rounded-full border-2 bg-transparent p-0 ${i ? "-ml-[7px]" : ""} ${
                              marcada && respDaMesa === p.chave ? "border-foreground" : "border-popover"
                            }`}
                          >
                            {p.chave === SEM_RESPONSAVEL ? (
                              <span className="block size-[20px] rounded-full border border-dashed border-terra/60" />
                            ) : p.chave === NA_IA ? (
                              <RostoDaIa tamanho={20} />
                            ) : (
                              <Rosto pessoa={p.nome} foto={p.foto} cor={p.cor} tamanho={20} />
                            )}
                          </button>
                        ))}
                      </span>
                      <span className="ml-1.5 min-w-[16px] text-right font-mono text-selo text-tinta-fraca tabular-nums">
                        {e.n}
                      </span>
                    </span>
                  </div>
                );
              })}
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/*
       * Os RECORTES de um toque, numa fileira só: quem espera resposta, o
       * arquivo e as etiquetas.
       *
       * As etiquetas eram uma nuvem aberta das seis mais usadas — com a produção
       * de verdade (23 etiquetas, centenas de casos cada) eram três linhas de
       * pílulas com "99+", quase 100px empurrando a primeira conversa para o
       * meio da tela. Viraram UM gatilho que abre a lista inteira, com a
       * contagem escrita; o nome da escolhida aparece nele, na cor dela.
       */}
      {/* `flex-wrap` é a rede, não o desenho: com espera, arquivo, número e
          etiqueta TODOS escolhidos no tablet a fileira quebra em vez de empurrar
          o último gatilho para fora da coluna. */}
      <div className={cn("flex flex-wrap items-center gap-1.5", celular ? "mt-1" : "mt-2.5")}>
        {esperando > 0 && (
          <button
            onClick={() => ajustar({ filtro: soEsperando ? null : "Prazo hoje" })}
            aria-pressed={soEsperando}
            aria-label={`Mostrar os ${esperando} esperando resposta`}
            data-dica={`${esperando} esperando resposta do escritório há mais de 15 min`}
            /* Ligada ela é TERRA e não tinta: é a fila de quem espera resposta,
               e terra é a cor da atenção. */
            className={cn(
              PILULA,
              "px-2.5 aria-pressed:border-terra aria-pressed:bg-veu-espera aria-pressed:text-terra aria-pressed:hover:bg-veu-espera aria-pressed:hover:text-terra",
            )}
          >
            <Icone de={ICONES.ampulheta} tamanho={14} />
            <span className={cn(CAPSULA, "group-aria-pressed:bg-terra group-aria-pressed:text-sobre-terra")}>
              {contagem(esperando)}
            </span>
          </button>
        )}

        {/* O ARQUIVO como pastilha, e não como interruptor escondido. Só
            aparece com caso dentro: pastilha que diz zero é um controle a mais. */}
        {(arquivados > 0 || soArquivados) && (
          <button
            onClick={() => ajustar({ filtro: soArquivados ? null : "Arquivados" })}
            aria-pressed={soArquivados}
            aria-label={`Mostrar os ${arquivados} atendimentos arquivados`}
            data-dica={`${arquivados} arquivado(s) — fora da mesa, mas guardados`}
            className={cn(PILULA, "px-2.5")}
          >
            <Icone de={ICONES.arquivar} tamanho={14} />
            <span className={CAPSULA}>{contagem(arquivados)}</span>
          </button>
        )}

        <div className="min-w-0 flex-1" />

        {/* O NÚMERO do setor, só quando há mais de um. Fechado é o ícone do
            WhatsApp e mais nada — numa fileira que já tem três pílulas, o nome
            só aparece quando o recorte está ligado (aí ele é informação). O
            desligado vem escrito no menu: filtrar "os do número que caiu" é o
            gesto de quem vai avisar esses clientes por outro caminho. */}
        {variosNumeros && (
          <MenuDeAcoes
            rotulo={numeroMarcado ? `Número ${numeroCurto(numeroMarcado)}. Trocar` : "Filtrar por número do WhatsApp"}
            titulo="Filtrar por número do WhatsApp"
            legenda="Números do setor"
            largura="260px"
            classe={cn(
              PILULA,
              "flex-initial gap-1 px-2.5 data-[popup-open]:bg-foreground/8 [&>svg]:flex-none",
              numeroMarcado && "border-tinta bg-tinta text-mesa hover:bg-tinta/86 hover:text-mesa",
            )}
            itens={[
              ...(numeroMarcado
                ? [
                    {
                      chave: "__todos__",
                      rotulo: "Todos os números",
                      icone: ICONES.fechar,
                      aoTocar: () => ajustar({ numero: null }),
                    },
                  ]
                : []),
              ...numerosDoSetor.map((n) => ({
                chave: String(n.id),
                rotulo: `${n.rotulo || "Número"}${n.padrao ? " · padrão" : ""}`,
                detalhe: `${numeroLegivel(n.numero) || "sem número"}${conectado(n) ? "" : " · desligado"}`,
                marca: numeroMarcado?.id === n.id ? "✓" : undefined,
                aoTocar: () => ajustar({ numero: numeroMarcado?.id === n.id ? null : String(n.id) }),
              })),
            ]}
          >
            <Icone de={ICONES.whatsapp} tamanho={14} />
            {numeroMarcado ? <span className="font-mono">{numeroCurto(numeroMarcado)}</span> : null}
            <Icone de={ICONES.baixo} tamanho={14} />
          </MenuDeAcoes>
        )}

        {(catalogoDeTags ?? []).length > 0 && (
          <MenuDeAcoes
            rotulo={tagDaMesa ? `Etiqueta: ${tagDaMesa}. Trocar` : "Filtrar por etiqueta"}
            titulo="Filtrar por etiqueta"
            legenda="Etiquetas"
            largura="300px"
            /* Teto que corta um item ao meio: a linha pela metade no pé é o que
               diz "rola", num menu sem barra visível. */
            altura="420px"
            /* Na ponta, longe das pastilhas de estado (o vão elástico acima), e
               encolhe com reticências se o nome da etiqueta for comprido. */
            classe={cn(PILULA, "min-w-0 flex-initial gap-1.5 px-3 data-[popup-open]:bg-foreground/8 [&>svg]:flex-none")}
            estilo={() => {
              if (!tagDaMesa) return "";
              const [fundo, letra] = tinta(tagDaMesa, tagMarcada?.cor);
              return `background:${fundo};color:${letra};border-color:transparent`;
            }}
            itens={[
              ...(tagDaMesa
                ? [
                    {
                      chave: "__todas__",
                      rotulo: "Todas as etiquetas",
                      icone: ICONES.fechar,
                      aoTocar: () => ajustar({ tag: null }),
                    },
                  ]
                : []),
              ...catalogoDeTags.map((c) => ({
                chave: c.tag,
                /* O número na PONTA da mesma linha, e não numa segunda: com
                   "N casos" embaixo cada item tinha 57px e o menu mostrava seis
                   das 23 etiquetas, sem nada dizendo que rolava. */
                rotulo: (
                  <span className="flex items-baseline gap-3">
                    <span className="min-w-0 truncate">{c.tag}</span>
                    <span className="ml-auto flex-none font-mono text-selo font-bold text-tinta-fraca tabular-nums">{c.n}</span>
                  </span>
                ),
                marca: tagDaMesa.toLowerCase() === c.tag.toLowerCase() ? "✓" : undefined,
                rosto: (
                  <span
                    aria-hidden="true"
                    className="size-2.5 flex-none rounded-full"
                    style={{ background: tinta(c.tag, c.cor)[2] }}
                  />
                ),
                aoTocar: () => ajustar({ tag: tagDaMesa.toLowerCase() === c.tag.toLowerCase() ? null : c.tag }),
              })),
            ]}
          >
            <Icone de={ICONES.etiqueta} tamanho={14} />
            {/* Sem etiqueta escolhida a palavra sai no tablet (coluna de 320) e
                quando o gatilho do número divide a fileira: os dois viram ícone
                com seta, lado a lado, e a palavra só aparece quando é recorte. */}
            {tagDaMesa || !(tablet || variosNumeros) ? (
              <span className="min-w-0 truncate">{tagDaMesa || "Etiquetas"}</span>
            ) : null}
            <Icone de={ICONES.baixo} tamanho={14} />
          </MenuDeAcoes>
        )}
      </div>

      {/* O recorte, escrito. Recorte que age é recorte que se vê. */}
      {ativo && (
        <div className="mt-2.5 flex min-h-padrao items-center gap-1.5 rounded-controle bg-verde/8 px-2.5 py-1.5 text-corpo font-semibold text-verde">
          Mostrando: {respDaMesa ? nomeResp : "todos"} · {etapaDaMesa ? rotuloEtapa : "qualquer etapa"}
          {tagDaMesa ? ` · etiqueta “${tagDaMesa}”` : ""}
          {numeroMarcado ? ` · número ${numeroCurto(numeroMarcado).replace("…", "final ")}` : ""}
          {/* O criativo vem da tela de Criativos, e não tem controle aqui: a
              linha escrita é a única pista de que a lista está recortada. */}
          {criativoDaMesa ? ` · ${criativoDaMesa === SEM_CRIATIVO ? "sem criativo" : `criativo “${criativoDaMesa}”`}` : ""} ·{" "}
          {/* Com a palavra: "final 4348 · 4210" lia como pedaço do telefone (Felipe, 02/11). */}
          {noRecorte.toLocaleString("pt-BR")} {noRecorte === 1 ? "caso" : "casos"}
          <button
            onClick={() => ajustar({ etapa: null, resp: null, tag: null, criativo: null, numero: null })}
            aria-label="Limpar o recorte"
            className="alvo ml-auto flex cursor-pointer items-center rounded-etiqueta border-0 bg-transparent p-0 text-tinta-fraca hover:text-foreground"
          >
            <Icone de={ICONES.fechar} tamanho={14} />
          </button>
        </div>
      )}
    </div>
  );
}
